import { useState, useEffect } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/icons/catering-icons/food_3.svg";
import SideBanner from "../../../examples/LayoutContainers/SideBanner";

import { registrationApi } from "../_request";
import { useAuth } from "context";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import { AnalyticsEvents } from "core/constant";
import { useAnalyticsEvent } from "hooks/useAnalyticsEvent";

function Cover() {
  const { saveAuthData, showSnackbarWithMessage } = useAuth();
  const navigate = useNavigate();
  const campaignCode = new URL(window.location.href).searchParams.get("code") || null;

  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [checkboxCheckedSMS, setCheckboxCheckedSMS] = useState(false);

  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [loader, setLoader] = useState(false);
  const { darkMode } = useAuth();
  const [registerData, setRegisterData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    workEmail: "",
    password: "",
    phoneNumber: "",
    role: "CUSTOMER",
  });

  const [nameError, setNameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const { trackCustomEvent } = useAnalyticsEvent();

  const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;

  const isEmailValid = (email) => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailPattern.test(email);
  };

  const handleCheckboxChange = (event) => {
    setCheckboxChecked(event.target.checked);
  };

  const handleSMSCheckboxChange = (event) => {
    setCheckboxCheckedSMS(event.target.checked);
  };

  const formatPhoneNumber = (value) => {
    value = value.replace(/\D/g, ""); // Remove all non-digit characters
    if (value.length > 10) value = value.slice(0, 10);

    if (!value || value.length === 0) return "";
    if (value.length <= 3) return `(${value}`;
    if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
  };

  const handlePhoneChange = (event) => {
    const formattedValue = formatPhoneNumber(event.target.value);
    setRegisterData((prevData) => ({
      ...prevData,
      phoneNumber: formattedValue,
    }));

    const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (formattedValue && !phonePattern.test(formattedValue)) {
      setPhoneError("Phone number must be in the format (XXX) XXX-XXXX");
    } else {
      setPhoneError("");
    }
  };

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;
    setRegisterData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "firstName") {
      if (value.trim().length < 3) {
        setNameError("First name should be at least 3 characters.");
      } else {
        setNameError("");
      }
    } else if (name === "password") {
      if (value.trim().length < 6) {
        setPasswordError("Password should be at least 6 characters.");
      } else {
        setPasswordError("");
      }
    } else if (name === "workEmail") {
      // Validate workEmail only if it's not an empty string
      if (value && !isEmailValid(value)) {
        setPasswordError("Work email is not valid.");
      } else {
        setPasswordError("");
      }
    }
  };

  useEffect(() => {
    updateButtonDisabledState();
  }, [checkboxChecked, registerData, checkboxCheckedSMS]);

  const updateButtonDisabledState = () => {
    const shouldDisableButton =
      !checkboxChecked ||
      !checkboxCheckedSMS ||
      registerData.firstName.trim() === "" ||
      registerData.email.trim() === "" ||
      registerData.password.trim() === "";
    setButtonDisabled(shouldDisableButton);
  };

  const handleSubmit = async () => {
    try {
      setLoader(true);
      const {
        data: { success, message, data },
      } = await registrationApi(
        {
          ...registerData,
        },
        campaignCode
      );
      if (success) {
        const verifiedCustomer = await saveAuthData(data.tokens);
        trackCustomEvent(
          AnalyticsEvents.SIGNUP,
          {
            method: "password",
            auth_origin: "customer_portal",
            user_id: verifiedCustomer.id,
            user_role: verifiedCustomer.role,
            user_type: verifiedCustomer.userType,
            ...(campaignCode && { campaign_code: campaignCode }),
          },
          false
        );
        navigate(`/authentication/verify-customer`);
        showSnackbarWithMessage({
          type: "success",
          message,
        });
        setLoader(false);
      } else {
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (error) {
      console.log("error", error.message);
      showSnackbarWithMessage({
        type: "error",
        message: error.message,
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <MDBox
      display={{
        xs: "flex",
        xl: "row",
        background: "linear-gradient(to right, rgba(1,233,98,0.1), rgba(0,123,255,0.1))",
      }}
      flexDirection={{
        xs: "column-reverse",
        xl: "row",
      }}
    >
      <SideBanner />
      <BasicLayout image={bgImage}>
        <Card sx={{ margin: 2, marginLeft: 3, maxWidth: "648px" }}>
          <MDBox mx={2} p={2} textAlign="center" minWidth={{ xs: "320px", xl: "380px" }}>
            <MDTypography variant="h4" fontWeight="medium" mt={1}>
              Sign up & start earning
            </MDTypography>
            <MDTypography display="block" variant="button" my={1}>
              Enter your email and mobile phone number to register
            </MDTypography>
          </MDBox>
          <MDBox pb={3} px={3}>
            <MDBox component="form" role="form">
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="First Name"
                  name="firstName"
                  variant="standard"
                  fullWidth
                  value={registerData.firstName}
                  onChange={handleFormFieldChange}
                  error={!!nameError} // Display error if nameError exists
                  helperText={nameError}
                  FormHelperTextProps={{ sx: { color: darkMode ? "red" : "" } }}
                  required
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Last Name"
                  name="lastName"
                  variant="standard"
                  fullWidth
                  value={registerData.lastName}
                  onChange={handleFormFieldChange}
                  helperText={nameError}
                  FormHelperTextProps={{ sx: { color: darkMode ? "red" : "" } }}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Personal Email"
                  name="email"
                  variant="standard"
                  fullWidth
                  value={registerData.email}
                  onChange={handleFormFieldChange}
                  error={!isEmailValid(registerData.email)}
                  required
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Work Email"
                  name="workEmail"
                  variant="standard"
                  fullWidth
                  value={registerData.workEmail}
                  onChange={handleFormFieldChange}
                  error={!isEmailValid(registerData.workEmail)}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="Password"
                  variant="standard"
                  name="password"
                  fullWidth
                  value={registerData.password}
                  onChange={handleFormFieldChange}
                  error={!!passwordError}
                  helperText={passwordError}
                  autoComplete="current-password"
                  required
                  FormHelperTextProps={{ sx: { color: darkMode ? "red" : "" } }}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDBox mb={2}>
                  <MDBox mb={2}>
                    <MDInput
                      type="tel"
                      label="Mobile Number"
                      variant="standard"
                      name="phoneNumber"
                      fullWidth
                      value={registerData.phoneNumber}
                      onChange={handlePhoneChange}
                      error={!!phoneError}
                      helperText={phoneError}
                    />
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                ml={-1}
                sx={{
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                }}
              >
                <Checkbox checked={checkboxChecked} onChange={handleCheckboxChange} />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  sx={{ userSelect: "none", gap: 1 }}
                >
                  I agree to&nbsp;
                </MDTypography>
                <MDTypography
                  component="a"
                  target="_blank"
                  href="https://cateringrewards.com/privacy_policy/"
                  variant="button"
                  fontWeight="bold"
                  color="info"
                  textGradient
                >
                  Privacy policy&nbsp;
                </MDTypography>
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  sx={{ userSelect: "none", gap: 1 }}
                >
                  and&nbsp;
                </MDTypography>
                <MDTypography
                  component="a"
                  target="_blank"
                  href="https://cateringrewards.com/terms_of_use/"
                  variant="button"
                  fontWeight="bold"
                  color="info"
                  textGradient
                >
                  Terms of use
                </MDTypography>
              </MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                ml={-1}
                sx={{
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                }}
              >
                <Checkbox checked={checkboxCheckedSMS} onChange={handleSMSCheckboxChange} />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  sx={{ userSelect: "none", gap: 1 }}
                >
                  I agree to receive alerts via email and&nbsp;
                </MDTypography>
                <MDTypography
                  component="a"
                  target="_blank"
                  href="https://cateringrewards.com/sms-terms/"
                  variant="button"
                  fontWeight="bold"
                  color="info"
                  textGradient
                >
                  SMS
                </MDTypography>
              </MDBox>
              <MDBox mt={4} mb={1}>
                <MDButton
                  variant="gradient"
                  color="success"
                  fullWidth
                  disabled={buttonDisabled || passwordError}
                  onClick={handleSubmit}
                >
                  sign up
                </MDButton>
              </MDBox>
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  Already have an account?{" "}
                  <MDTypography
                    component={Link}
                    to="/authentication/sign-in"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Sign In
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
        {loader && <SimpleBackdrop />}
      </BasicLayout>
    </MDBox>
  );
}

export default Cover;
